/**
 * 2026-10-07 [신설] 네이티브 앱이 웹뷰 안의 웹앱을 **다시 불러오지 않고** 다른 화면으로 옮기는 창구.
 * (마퍼가 앱 백로그 3-3)
 *
 * ## 왜 필요한가
 * 앱은 인앱결제가 막혀 일반 결제로 물러설 때 웹뷰 주소를 통째로 갈아끼웠다(`loadRequest`).
 * 이 웹앱은 SPA 라 화면만 바꾸면 되는데, 그렇게 하면 **처음부터 다시 받는다.** 느린 망(중국 현장)에서는
 * 그대로 대기 시간이고, 보던 화면의 상태도 잃는다. 이 함수로 화면만 옮긴다.
 *
 * ## 앱이 호출하는 방법 (앱팀 연동 규약)
 * **반환값을 받아야 한다** — 안드로이드 `evaluateJavascript` · iOS `evaluateJavaScript`
 * (Flutter `runJavaScriptReturningResult`).
 *
 * ```js
 * window.dyAppNavigate('https://www.myfirstguide.com/v/payment/step1?typedPks=tg1')  // → true | false
 * ```
 *
 * - **true** = 받아서 이동을 시작했다. **false** = 받지 않았다 → 앱은 종전처럼 주소를 통째로 갈아끼운다.
 * - 함수가 **없으면**(이 플러그인 전의 옛 웹앱) 앱이 같은 폴백을 탄다. 그래서 «받았다» 는 이 반환값으로만
 *   판단한다 — 신호만 던지고 끝내면 못 받았을 때 조용히 아무 일도 안 일어나 결제가 멈춘다.
 * - 앱 쪽 구현: 공용 플러그인 `flutter_plugin_doyac/lib/web_view/web_app_navigator.dart`.
 *
 * ## 받는 조건 — 하나라도 어긋나면 false
 * - **우리 사이트 주소**다: 지금 호스트 · 설정 도메인(www) · 차단 우회 별칭(web-mfg 등) 중 하나.
 *   별칭 호스트에서 www 주소가 와도 받는다 — 화면만 옮기므로 **지금 호스트에 그대로 머문다**(원하는 동작).
 * - **웹앱 경로**(`basePath`, 예 `/v/...`) 아래다. 구 PHP 페이지는 SPA 가 못 그린다.
 * - **그 경로에 맞는 페이지가 있다.** 전부 받는 404 라우트가 없어서 `matched` 로 가를 수 있다.
 *
 * ## 함정
 * 🔴 앱에 true 를 돌려준 뒤 이동이 실패하면(화면 조각 로드 실패 등) 같은 호스트로 **통째로 이동**해 마저 연다 —
 *    앱은 이미 끝났다고 보므로 여기서 책임진다.
 * 🔴 구매 직후처럼 웹앱 상태가 낡는 이동에는 앱이 이 함수를 쓰지 않는다(구매 목록은 로그인 때만 받는다).
 * 🔴 «앱 판별» 용도가 아니다. 모든 브라우저에 정의된다.
 */
export default defineNuxtPlugin(() => {
  if (!import.meta.client) return

  const router = useRouter()
  // 별칭 덮어쓰기 **전** 원래 설정을 본다 — useDYAppConfig() 는 별칭 호스트에서 fullDomain 을 별칭으로 바꾼다.
  const rawConfig = useAppConfig() as unknown as { fullDomain?: string, domainAliases?: Record<string, unknown> }
  // '/v/' → '/v', '/' → ''
  const base = (useRuntimeConfig().app.baseURL || '/').replace(/\/+$/, '')

  const ourHosts = new Set<string>([window.location.host])
  try {
    if (rawConfig.fullDomain) ourHosts.add(new URL(rawConfig.fullDomain).host)
  } catch (e) {
    // 설정이 이상해도 지금 호스트만으로 동작한다.
  }
  for (const alias of Object.keys(rawConfig.domainAliases ?? {})) ourHosts.add(alias)

  window.dyAppNavigate = (target: string): boolean => {
    try {
      if (typeof target !== 'string' || !target) return false

      const url = new URL(target, window.location.href)
      if (!ourHosts.has(url.host)) return false
      if (base && url.pathname !== base && !url.pathname.startsWith(base + '/')) return false

      const path = (url.pathname.slice(base.length) || '/') + url.search + url.hash
      const resolved = router.resolve(path)
      if (!resolved.matched.length) return false

      router.push(path).catch(() => {
        // resolved.href 는 basePath 를 포함한 같은 호스트 주소다.
        window.location.assign(resolved.href)
      })
      return true
    } catch (e) {
      return false
    }
  }
})
